import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

export function ShopInsuranceLicensingSheet({ children }: { children: React.ReactNode }) {
  return (
    <Sheet>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="flex flex-col gap-y-8 overflow-y-scroll pb-24">
        <SheetHeader>
          <SheetTitle>Licensing & Jurisdiction Details</SheetTitle>
          <SheetDescription>
            Wealth Assurance Group, LLC, a wholly owned subsidiary of Waterlily, holds resident and
            non-resident insurance producer licenses in 43 states and Washington D.C. A
            state-by-state list of license numbers is provided at the end of this notice.
            <br />
            <br />
            Waterlily is a software provider. It is <strong>not</strong> an insurance company and{' '}
            <strong>does not</strong> itself issue, underwrite or guarantee any insurance coverage.
          </SheetDescription>
        </SheetHeader>
        <SheetHeader>
          <SheetTitle>Purpose of This Material</SheetTitle>
          <SheetDescription>
            This website is an <strong>invitation to inquire</strong> about insurance. Neither the
            information presented nor any quote displayed constitutes a contract, a formal offer of
            coverage or a guarantee of issue. Coverage becomes effective only after a policy is
            issued by the carrier, premium is accepted and any delivery requirements are satisfied.
          </SheetDescription>
        </SheetHeader>
        <SheetHeader>
          <SheetTitle>How We Are Paid</SheetTitle>
          <SheetDescription>
            If a policy is issued, licensed producer(s) listed on the final application may receive
            commission and/or override compensation from the issuing carrier. If Wealth Assurance
            Group serves as a producer on your application, they may receive part or all of that
            commission. Compensation does not affect your premium. You may request additional
            information about the amount, nature or method of any compensation at any time.
          </SheetDescription>
        </SheetHeader>
        <SheetHeader>
          <SheetTitle>AI & Data Use</SheetTitle>
          <SheetDescription>
            Waterlily and Wealth Assurance Group use artificial-intelligence–powered software to (i)
            present illustrative quotes, (ii) map your intake responses onto carrier application
            forms, and (iii) suggest underwriting classifications. Human review is always required.
            You remain responsible for confirming that every answer is complete and accurate.
          </SheetDescription>
        </SheetHeader>
        <SheetHeader>
          <SheetTitle>Privacy</SheetTitle>
          <SheetDescription>
            Waterlily and Wealth Assurance Group collects personal, financial and health information
            to generate quotes and facilitate underwriting. That information is protected
            information in accordance with the Waterlily Privacy Notice and applicable federal and
            state privacy statutes (GLBA, CCPA/CPRA, HIPAA, etc.). By continuing, you consent to
            this collection and to electronic delivery of disclosures pursuant to ESIGN.
          </SheetDescription>
        </SheetHeader>
        <SheetHeader>
          <SheetTitle>State-Specific Disclosures</SheetTitle>
          <SheetDescription>
            California Residents - Pursuant to Cal. Ins. Code § 789.8, you may request a written
            estimate of any commissions that may be paid on the sale of a long-term-care insurance
            policy.
            <br />
            <br />
            New York Residents - Producer compensation information will be provided not later than
            the time of application, in accordance with NY Ins. Law § 30 (2119).
          </SheetDescription>
        </SheetHeader>
        <SheetHeader>
          <SheetTitle>Wealth Assurance Group&apos;s Licenses</SheetTitle>
          <SheetDescription>
            Businesses that give you advice and sell you insurance are required to be licensed by
            each state in which they operate. All solicitations to apply for insurance and
            applications for insurance are made through Wealth Assurance Group, LLC, which holds the
            licenses listed below:

            <ul className="my-0.5 list-disc space-y-0.5 p-4">
              <li>Alabama (AL): 3003729747</li>
              <li>Alaska (AK): 3003734035</li>
              <li>Arizona (AZ): 3003729750</li>
              <li>Arkansas (AR): 3003729738</li>
              <li>California (CA): Licensing in progress</li>
              <li>Colorado (CO): 901161</li>
              <li>Connecticut (CT): 3003729733</li>
              <li>Delaware (DE): 3003729748</li>
              <li>District of Columbia (DC): 3003729745</li>
              <li>Florida (FL): Licensing in progress</li>
              <li>Georgia (GA): 241623</li>
              <li>Hawaii (HI): 3003729698</li>
              <li>Idaho (ID): 3003730163</li>
              <li>Illinois (IL): 3003729766</li>
              <li>Indiana (IN): 4127845</li>
              <li>Iowa (IA): 3003729723</li>
              <li>Kansas (KS): 3003729740</li>
              <li>Kentucky (KY): DOI-1409264</li>
              <li>Louisiana (LA): 1206153</li>
              <li>Maine (ME): AGN520284</li>
              <li>Maryland (MD): 3003729765</li>
              <li>Massachusetts (MA): Licensing in progress</li>
              <li>Michigan (MI): 0155815</li>
              <li>Minnesota (MN): 40986261</li>
              <li>Mississippi (MS): Licensing in progress</li>
              <li>Missouri (MO): 3003768116</li>
              <li>Montana (MT): 3003729741</li>
              <li>Nebraska (NE): 3003729711</li>
              <li>Nevada (NV): 4106289</li>
              <li>New Hampshire (NH): 3003729709</li>
              <li>New Jersey (NJ): 3003729708</li>
              <li>New Mexico (NM): Licensing in progress</li>
              <li>New York (NY): Licensing in progress</li>
              <li>North Carolina (NC): 3003729735</li>
              <li>North Dakota (ND): 3003729763</li>
              <li>Ohio (OH): 1670268</li>
              <li>Oklahoma (OK): 3003729768</li>
              <li>Oregon (OR): Licensing in progress</li>
              <li>Pennsylvania (PA): 1259851</li>
              <li>Rhode Island (RI): 3003729772</li>
              <li>South Carolina (SC): 3003729717</li>
              <li>South Dakota (SD): 21570741</li>
              <li>Tennessee (TN): 3003729722</li>
              <li>Texas (TX): 3360065</li>
              <li>Utah (UT): 1080906</li>
              <li>Vermont (VT): 3003729727</li>
              <li>Virginia (VA): 163791</li>
              <li>Washington (WA): 1314084</li>
              <li>West Virginia (WV): 3003729974</li>
              <li>Wisconsin (WI): 3003729710</li>
              <li>Wyoming (WY): 636769</li>
            </ul>
          </SheetDescription>
        </SheetHeader>
      </SheetContent>
    </Sheet>
  );
}
